"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton for a single carousel slide
 */
export interface SlideSkeletonProps {
  className?: string;
  /** Aspect ratio (e.g., "8/5", "16/9", "1/1") */
  aspectRatio?: string;
  /** Show content skeleton below image */
  showContent?: boolean;
  /** Number of content lines */
  contentLines?: number;
}

export function SlideSkeleton({
  className,
  aspectRatio = "8/5",
  showContent = false,
  contentLines = 3,
}: SlideSkeletonProps) {
  return (
    <div className={cn("space-y-3", className)}>
      <Skeleton
        className="w-full rounded-lg"
        style={{ aspectRatio }}
      />
      {showContent && (
        <div className="space-y-2">
          {Array.from({ length: contentLines }).map((_, i) => (
            <Skeleton
              key={i}
              className={cn(
                "h-4 rounded",
                i === contentLines - 1 && "w-3/4"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton for accommodation card carousel
 */
export function AccommodationCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      {/* Image skeleton */}
      <Skeleton className="w-full aspect-[8/5] md:aspect-[135/100] rounded-lg" />
      
      {/* Content skeleton */}
      <div className="space-y-2">
        {/* Location and rating */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-8" />
        </div>
        {/* Title */}
        <Skeleton className="h-5 w-full" />
        {/* Capacity */}
        <Skeleton className="h-4 w-32" />
        {/* Price */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-24" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for story bubbles
 */
export function StoryBubbleSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col items-center gap-[18px]", className)}>
      <Skeleton className="size-16 md:size-[5.25rem] rounded-full" />
      <Skeleton className="h-3 w-12" />
    </div>
  );
}

/**
 * Skeleton for category items
 */
export function CategorySkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <Skeleton className="size-12 md:size-16 rounded-full" />
      <Skeleton className="h-3 w-14" />
    </div>
  );
}

/**
 * Skeleton for blog cards
 */
export function BlogCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      <Skeleton className="w-full aspect-[11/7] md:aspect-[3/2] rounded-lg" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
}

/**
 * Skeleton for destination cards
 */
export function DestinationSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border rounded-[8px] md:rounded-[12px] p-2 md:p-3 border-gray-100",
        className
      )}
    >
      <Skeleton className="w-16 md:w-[72px] aspect-[1.1/1] rounded-[13px] shrink-0" />
      <div className="flex flex-col gap-1 flex-1">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  );
}

/**
 * Skeleton for thumbnail images
 */
export function ThumbnailSkeleton({ className }: { className?: string }) {
  return (
    <Skeleton
      className={cn(
        "!w-[106px] md:!w-[121px] !h-[96px] md:!h-[100px] rounded-lg",
        className
      )}
    />
  );
}

/**
 * Generic carousel skeleton with multiple slides
 */
export interface CarouselSkeletonProps {
  /** Number of skeleton slides to show */
  slideCount?: number;
  /** Custom slide skeleton component */
  slideComponent?: React.ComponentType<{ className?: string }>;
  /** Gap between slides */
  gap?: number;
  /** Custom class for container */
  className?: string;
  /** Custom class for each slide */
  slideClassName?: string;
}

export function CarouselSkeleton({
  slideCount = 4,
  slideComponent: SlideComponent = SlideSkeleton,
  gap = 16,
  className,
  slideClassName,
}: CarouselSkeletonProps) {
  return (
    <div className={cn("flex overflow-hidden", className)} style={{ gap }}>
      {Array.from({ length: slideCount }).map((_, i) => (
        <div key={i} className={cn("shrink-0", slideClassName)}>
          <SlideComponent />
        </div>
      ))}
    </div>
  );
}

/**
 * Image skeleton with loading state
 */
export function ImageSkeleton({
  className,
  aspectRatio = "16/9",
}: {
  className?: string;
  aspectRatio?: string;
}) {
  return (
    <Skeleton
      className={cn("w-full rounded-lg", className)}
      style={{ aspectRatio }}
    />
  );
}

